import {
  categoryStructure,
  Category,
  getAllCategories,
  getCategoryName,
} from "@/utils/categories";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Briefcase,
  Globe,
  GraduationCap,
  Heart,
  Music,
  Newspaper,
  ShoppingBag,
  Tv,
} from "lucide-react";

export const BUNDESLAENDER: string[] = [
  "Baden-Württemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thüringen",
  "Überregional",
];

export const FEDERAL_STATE_OPTIONS = [...BUNDESLAENDER];

export interface MockWebsite {
  id: string;
  title: string;
  url: string;
  description: string;
  mainCategory: string;
  subCategory: string;
  categories: string[];
  region: string;
  zipCode: string;
  addedBy: string;
  addedAt: string;
  date: string;
  dateAdded: string;
}

export interface ThematicSubCategory {
  name: string;
  count: number;
  subcategories?: ThematicSubCategory[];
}

export interface ThematicCategory extends ThematicSubCategory {
  icon: LucideIcon;
}

export interface ThematicSite {
  id: string;
  title: string;
  url: string;
  description: string;
  region: string;
  date: string;
  categories: string[];
}

export interface DistributionEntry {
  name: string;
  count: number;
}

export interface MockDataSummary {
  totalWebsites: number;
  totalCategories: number;
  avgCategoriesPerWebsite: number;
  categoryDistribution: DistributionEntry[];
  regionDistribution: DistributionEntry[];
}

const TOTAL_RECORDS = 12_345;
const BASE_TIMESTAMP = Date.UTC(2022, 0, 1);
const PATH_SEPARATOR = " > ";

let cache: MockWebsite[] | null = null;
let categoryCountCache: Record<string, number> | null = null;
let datasetSummaryCache: MockDataSummary | null = null;
let thematicSiteCache: ThematicSite[] | null = null;
let thematicCategoryCountCache: Record<string, number> | null = null;

export function getMockWebsites(): MockWebsite[] {
  if (!cache) {
    cache = buildMockWebsites();
  }
  return cache;
}

export function getCategoryCounts(): Record<string, number> {
  if (!categoryCountCache) {
    // Ensure dataset (and counts) is built
    getMockWebsites();
  }
  return categoryCountCache || {};
}

export function getThematicSites(): ThematicSite[] {
  ensureThematicDataset();
  return thematicSiteCache || [];
}

export function getThematicCategoryCounts(): Record<string, number> {
  ensureThematicDataset();
  return thematicCategoryCountCache || {};
}

export function getMockDataSummary(): MockDataSummary {
  if (datasetSummaryCache) {
    return datasetSummaryCache;
  }

  const websites = getMockWebsites();
  const totalWebsites = websites.length;
  const totalCategories = getAllCategories().length;

  let categoryAssignments = 0;
  const categoryDistributionMap = new Map<string, number>();
  const regionDistributionMap = new Map<string, number>();

  websites.forEach((site) => {
    categoryAssignments += site.categories.length;
    const main = site.mainCategory || site.categories[0] || "Sonstige";
    categoryDistributionMap.set(
      main,
      (categoryDistributionMap.get(main) || 0) + 1
    );
    regionDistributionMap.set(
      site.region,
      (regionDistributionMap.get(site.region) || 0) + 1
    );
  });

  const avgCategoriesPerWebsite =
    totalWebsites > 0 ? categoryAssignments / totalWebsites : 0;

  const categoryDistribution = Array.from(categoryDistributionMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const regionDistribution = Array.from(regionDistributionMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  datasetSummaryCache = {
    totalWebsites,
    totalCategories,
    avgCategoriesPerWebsite,
    categoryDistribution,
    regionDistribution,
  };
  return datasetSummaryCache;
}

function buildMockWebsites(): MockWebsite[] {
  const combos = flattenCategories(categoryStructure);
  const records: MockWebsite[] = [];
  const counts: Record<string, number> = {};
  for (let i = 0; i < TOTAL_RECORDS; i += 1) {
    const combo = combos[i % combos.length];
    const region = BUNDESLAENDER[i % BUNDESLAENDER.length];
    const timestamp = BASE_TIMESTAMP + i * 60 * 60 * 1000; // +1h per entry
    const iso = new Date(timestamp).toISOString();
    const record: MockWebsite = {
      id: `mock-${i + 1}`,
      title: `${combo.fullLabel} Portal ${i + 1}`,
      url: `https://${combo.slug}-${i + 1}.wissen.de`,
      description: `Digitales Informationsangebot zu ${combo.fullLabel} mit Fokus auf ${region}.`,
      mainCategory: combo.mainCategory,
      subCategory: combo.subCategory || combo.mainCategory,
      categories: combo.path,
      region,
      zipCode: String(10000 + (i % 90000)).padStart(5, "0"),
      addedBy: `${combo.slug}@bundesarchiv.de`,
      addedAt: iso,
      date: iso,
      dateAdded: iso,
    };

    incrementCounts(combo.path, counts);
    records.push(record);
  }
  categoryCountCache = counts;
  return records;
}

function ensureThematicDataset() {
  if (!thematicSiteCache || !thematicCategoryCountCache) {
    const { sites, counts } = generateThematicDataset();
    thematicSiteCache = sites;
    thematicCategoryCountCache = counts;
  }
}

type CategoryCombo = {
  path: string[];
  mainCategory: string;
  subCategory: string;
  fullLabel: string;
  slug: string;
};

function flattenCategories(
  nodes: Category[],
  parentPath: string[] = []
): CategoryCombo[] {
  const combos: CategoryCombo[] = [];
  nodes.forEach((node) => {
    const label = getCategoryName(node, "de");
    const path = [...parentPath, label];
    combos.push({
      path,
      mainCategory: path[0],
      subCategory: path[1],
      fullLabel: path.join(" > "),
      slug: slugify(path.join("-")),
    });
    if (node.subcategories?.length) {
      combos.push(...flattenCategories(node.subcategories, path));
    }
  });
  return combos;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function incrementCounts(path: string[], counts: Record<string, number>) {
  for (let i = 1; i <= path.length; i += 1) {
    const key = path.slice(0, i).join(PATH_SEPARATOR);
    counts[key] = (counts[key] || 0) + 1;
  }
}

export const baseThematicCategories: ThematicCategory[] = [
  {
    name: "Wissenschaft & Forschung",
    icon: GraduationCap,
    count: 1247,
    subcategories: [
      {
        name: "Physik",
        count: 234,
        subcategories: [
          {
            name: "Theoretische Physik",
            count: 89,
            subcategories: [
              { name: "Quantenfeldtheorie", count: 24 },
              { name: "Allgemeine Relativitätstheorie", count: 18 },
              { name: "Statistische Physik", count: 16 },
              { name: "Stringtheorie", count: 12 },
            ],
          },
          {
            name: "Experimentelle Physik",
            count: 72,
            subcategories: [
              { name: "Festkörperphysik", count: 21 },
              { name: "Teilchenphysik", count: 19 },
              { name: "Atom- & Molekülphysik", count: 14 },
              { name: "Optik & Photonik", count: 10 },
            ],
          },
          { name: "Angewandte Physik", count: 40 },
        ],
      },
      {
        name: "Biologie",
        count: 198,
        subcategories: [
          { name: "Genetik", count: 55 },
          { name: "Mikrobiologie", count: 41 },
          { name: "Neurobiologie", count: 36 },
          { name: "Ökologie", count: 30 },
        ],
      },
      {
        name: "Informatik",
        count: 301,
        subcategories: [
          {
            name: "Künstliche Intelligenz",
            count: 110,
            subcategories: [
              { name: "Maschinelles Lernen", count: 60 },
              { name: "NLP", count: 22 },
              { name: "Computer Vision", count: 20 },
            ],
          },
          { name: "Software Engineering", count: 78 },
          { name: "Theoretische Informatik", count: 45 },
        ],
      },
      { name: "Chemie", count: 145 },
      { name: "Mathematik", count: 156 },
    ],
  },
  {
    name: "Bildung & Kultur",
    icon: BookOpen,
    count: 2103,
    subcategories: [
      {
        name: "Universitäten",
        count: 389,
        subcategories: [
          { name: "Staatliche Universitäten", count: 260 },
          { name: "Private Universitäten", count: 74 },
          {
            name: "Fakultäten",
            count: 55,
            subcategories: [
              { name: "Mathe/Info", count: 20 },
              { name: "WiWi", count: 14 },
              { name: "Medizin", count: 12 },
            ],
          },
        ],
      },
      {
        name: "Schulen",
        count: 456,
        subcategories: [
          { name: "Grundschulen", count: 180 },
          { name: "Gymnasien", count: 160 },
          { name: "Berufsschulen", count: 80 },
        ],
      },
      {
        name: "Museen",
        count: 210,
        subcategories: [
          { name: "Kunstmuseen", count: 90 },
          { name: "Technikmuseen", count: 55 },
          { name: "Naturkundemuseen", count: 40 },
        ],
      },
      { name: "Bibliotheken", count: 178 },
    ],
  },
  {
    name: "Wirtschaft & Finanzen",
    icon: Briefcase,
    count: 1856,
    subcategories: [
      {
        name: "Unternehmen",
        count: 567,
        subcategories: [
          { name: "DAX-Unternehmen", count: 40 },
          { name: "Mittelstand", count: 380 },
          { name: "Startups", count: 120 },
        ],
      },
      {
        name: "Banken",
        count: 280,
        subcategories: [
          { name: "Großbanken", count: 22 },
          { name: "Landesbanken", count: 16 },
          { name: "Sparkassen", count: 180 },
          { name: "Genossenschaftsbanken", count: 60 },
        ],
      },
      { name: "Versicherungen", count: 210 },
      { name: "Förderprogramme", count: 98 },
    ],
  },
  {
    name: "Medien & Nachrichten",
    icon: Newspaper,
    count: 934,
    subcategories: [
      {
        name: "Zeitungen",
        count: 298,
        subcategories: [
          { name: "Überregional", count: 60 },
          { name: "Regional", count: 220 },
          { name: "Fachpresse", count: 18 },
        ],
      },
      { name: "TV & Radio", count: 320 },
      { name: "Online-Portale", count: 210 },
    ],
  },
  {
    name: "Gesundheit & Medizin",
    icon: Heart,
    count: 1521,
    subcategories: [
      {
        name: "Krankenhäuser",
        count: 389,
        subcategories: [
          { name: "Universitätskliniken", count: 35 },
          { name: "Kommunale Kliniken", count: 220 },
          { name: "Private Kliniken", count: 90 },
        ],
      },
      { name: "Ärzte & Praxen", count: 720 },
      { name: "Forschungseinrichtungen", count: 120 },
    ],
  },
  {
    name: "Unterhaltung & Medien",
    icon: Tv,
    count: 1089,
    subcategories: [
      {
        name: "Film",
        count: 267,
        subcategories: [
          { name: "Kinos", count: 120 },
          {
            name: "Festivals",
            count: 25,
          },
        ],
      },
      { name: "Streaming", count: 210 },
      { name: "Gaming", count: 180 },
    ],
  },
  {
    name: "Musik & Kunst",
    icon: Music,
    count: 876,
    subcategories: [
      {
        name: "Klassik",
        count: 178,
        subcategories: [
          { name: "Orchester", count: 40 },
          {
            name: "Oper",
            count: 22,
          },
        ],
      },
      { name: "Pop/Rock", count: 220 },
      { name: "Museen & Galerien", count: 160 },
    ],
  },
  {
    name: "Sport & Freizeit",
    icon: Globe,
    count: 1432,
    subcategories: [
      {
        name: "Fußball",
        count: 389,
        subcategories: [
          { name: "Bundesliga", count: 36 },
          { name: "Regionalligen", count: 180 },
          { name: "Vereine", count: 150 },
        ],
      },
      {
        name: "Outdoor",
        count: 260,
        subcategories: [
          {
            name: "Wandern",
            count: 120,
          },
          { name: "Klettern", count: 40 },
        ],
      },
    ],
  },
  {
    name: "Shopping & E-Commerce",
    icon: ShoppingBag,
    count: 1789,
    subcategories: [
      {
        name: "Online-Shops",
        count: 987,
        subcategories: [
          { name: "Elektronik", count: 220 },
          {
            name: "Mode",
            count: 260,
          },
        ],
      },
      { name: "Preisvergleich", count: 210 },
      { name: "Marktplätze", count: 320 },
    ],
  },
];

export function buildMockResultsFromCategories(): ThematicSite[] {
  return getThematicSites();
}

function generateThematicDataset(): {
  sites: ThematicSite[];
  counts: Record<string, number>;
} {
  const templates: { path: string[]; slug: string }[] = [];
  const startTs = Date.UTC(2025, 0, 1);
  const endOfYearTs = Date.UTC(2025, 11, 31, 23, 59, 59);
  const todayTs = Date.now();
  const effectiveEndTs = Math.min(endOfYearTs, Math.max(startTs, todayTs));
  let counter = 1;
  const counts: Record<string, number> = {};

  const randomDate = () => {
    if (effectiveEndTs <= startTs) {
      return new Date(startTs).toISOString();
    }
    const delta = effectiveEndTs - startTs;
    const ts = startTs + Math.floor(Math.random() * delta);
    return new Date(ts).toISOString();
  };

  const randomRegion = () =>
    BUNDESLAENDER[Math.floor(Math.random() * BUNDESLAENDER.length)] ||
    "Bundesweit";

  const slugifyPath = (path: string[]) =>
    path
      .join("-")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const collectTemplates = (
    nodes: (ThematicCategory | ThematicSubCategory)[],
    trail: string[] = []
  ) => {
    nodes.forEach((node) => {
      const nextPath = [...trail, node.name];
      templates.push({ path: nextPath, slug: slugifyPath(nextPath) });
      if (node.subcategories?.length) {
        collectTemplates(node.subcategories, nextPath);
      }
    });
  };

  collectTemplates(baseThematicCategories);

  const multiplier = 100;
  const results: ThematicSite[] = [];

  templates.forEach((template) => {
    for (let i = 0; i < multiplier; i += 1) {
      const suffix = i + 1;
      const site: ThematicSite = {
        id: `cat-${counter++}`,
        title: `${template.path[template.path.length - 1]} Portal ${suffix}`,
        url: `https://${template.slug || "themen"}-${suffix}.webkurator.de`,
        description: `Kuratiertes Angebot für ${template.path.join(
          " > "
        )} (${suffix}).`,
        region: randomRegion(),
        date: randomDate(),
        categories: template.path,
      };
      results.push(site);
      incrementCounts(template.path, counts);
    }
  });

  return { sites: results, counts };
}


